import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  ClipboardCheck,
  HandHelping,
  ShieldCheck,
  Stethoscope,
  Users,
} from "lucide-react";
import {
  AUSTRALIA_CARE_WORKFORCE_APPLICATIONS_OPEN,
  AUSTRALIA_CARE_WORKFORCE_INTAKE_FORM,
} from "../constants";

const programSteps = [
  {
    title: "Submit Your Intake Form",
    description:
      "Complete the intake form with accurate details about your background, experience, communication skills, and readiness.",
  },
  {
    title: "Initial Screening",
    description:
      "Applications are reviewed for initial suitability. Shortlisted candidates may be contacted for communication checks, document review, and further screening.",
  },
  {
    title: "Training and Preparation",
    description:
      "Candidates who continue may go through structured preparation covering care-support roles, workplace expectations, communication, and readiness.",
  },
  {
    title: "Assessment",
    description:
      "Candidates may be assessed through practical, communication, behavioral, or role-readiness evaluations.",
  },
  {
    title: "Employer Review or Matching",
    description:
      "Candidates who meet the required standard may be profiled for suitable employer opportunities in Australia, where available.",
  },
  {
    title: "Next-Stage Guidance",
    description:
      "Candidates who progress may receive guidance on interviews, documentation, compliance, and later-stage requirements.",
  },
];

const programIncludes = [
  "structured candidate intake",
  "screening and suitability review",
  "communication and readiness checks",
  "preparation and training support",
  "role orientation",
  "assessment and evaluation",
  "interview preparation where relevant",
  "candidate presentation for employer consideration",
  "guidance on next steps for candidates who progress",
];

const suitabilityPoints = [
  "have an interest in care-related work",
  "have experience in elderly care, disability support, home care, hospital support, community care, or related service roles",
  "are comfortable with personal care responsibilities where required",
  "can communicate in English at a functional professional level",
  "are open to structured training and assessment",
  "are willing to follow a formal process",
  "are open to relocation if selected and if all requirements are met",
];

const ethicalPrinciples = [
  {
    icon: ShieldCheck,
    title: "Transparency",
    description:
      "We explain the program clearly, including what is being offered and what remains subject to review.",
  },
  {
    icon: HandHelping,
    title: "Fair Treatment",
    description:
      "Candidates should experience respectful communication, professional screening, and clear expectations.",
  },
  {
    icon: Users,
    title: "Worker Protection",
    description:
      "The process is designed to reduce misleading expectations and support informed participation.",
  },
  {
    icon: BadgeCheck,
    title: "Responsible Process Design",
    description:
      "Progression is not automatic and remains subject to employer, legal, and documentation requirements.",
  },
];

const commitments = [
  {
    title: "Clear and Honest Communication",
    description:
      "We will communicate the program clearly and will not present intake as a guaranteed job or migration outcome.",
  },
  {
    title: "No Misleading Claims",
    description:
      "We will avoid exaggerated or deceptive claims, including promises of guaranteed employment, visas, or relocation.",
  },
  {
    title: "Transparency on Process Stages",
    description:
      "We will explain the major stages clearly so candidates understand what is being offered and what is still under review.",
  },
  {
    title: "Respect for Candidate Dignity",
    description:
      "Candidates should be treated with respect, professionalism, and fairness throughout the process.",
  },
  {
    title: "Informed Decision-Making",
    description:
      "Candidates should have enough information to make informed decisions before moving forward.",
  },
  {
    title: "Responsible Handling of Expectations",
    description:
      "We distinguish clearly between intake, preparation, assessment, employer consideration, and any actual job offer.",
  },
  {
    title: "Protection Against Exploitative Practices",
    description:
      "We aim to reduce the risk of misinformation, coercion, unfair treatment, or exploitative recruitment behavior.",
  },
  {
    title: "Respect for Applicable Legal and Employer Requirements",
    description:
      "Any move into employment-related stages remains subject to employer, regulatory, and destination-country requirements.",
  },
];

const safeguards = [
  "clear explanation of the program purpose",
  "clear explanation that intake does not equal employment",
  "clear explanation of the stages before any progression",
  "communication of key conditions before major decisions are made",
  "professional treatment during screening and assessment",
  "opportunity to understand what is being evaluated",
  "clear distinction between preparation stages and any later employer-led stage",
];

const eligibilitySnapshot = [
  "valid identification and supporting documentation",
  "ability to complete the intake process truthfully and accurately",
  "willingness to participate in screening and assessment",
  "ability to communicate in English",
  "readiness for structured preparation or training",
  "professionalism and willingness to follow program instructions",
  "openness to relocation if selected",
  "ability to satisfy later employer and administrative requirements if progressed",
];

const faqs = [
  {
    question: "Is this a job offer?",
    answer:
      "No. This program is for candidate intake, screening, and possible progression through a structured program. It is not a job offer, and it should not be understood as a promise of employment, sponsorship, or relocation.",
  },
  {
    question: "Does submitting the form guarantee placement?",
    answer:
      "No. Submitting the intake form only means your details will be reviewed within the program process. It does not guarantee progression, placement, sponsorship, relocation, or employment. Advancement depends on screening outcomes, assessment performance, suitability, available opportunities, and employer requirements.",
  },
  {
    question: "Is participation free?",
    answer:
      "At the current intake stage, participation is intended to be fully sponsored for selected candidates. If any later stage involves third-party costs, such as documentation, medical, travel, regulatory, or destination-country requirements, those should be communicated clearly before a candidate decides whether to proceed.",
  },
  {
    question: "What roles is this for?",
    answer:
      "The present focus is on Health Care Assistant and related care-support pathways connected to Australia. Depending on employer needs, this may include care environments such as aged care, disability support, home care, hospital support, or similar service settings.",
  },
  {
    question: "What happens after I submit the form?",
    answer:
      "After submission, your intake will be reviewed to determine initial suitability. Candidates who are shortlisted may be contacted for further screening, communication checks, document review, or the next stage of the process. Not every applicant will progress beyond the initial review stage.",
  },
  {
    question: "Do I need prior experience?",
    answer:
      "Relevant experience can be helpful, especially in elderly care, disability support, home care, hospital support, community care, or related service roles. However, progression is based on overall suitability, communication ability, readiness, assessment outcomes, and the needs of the pathway or participating employers.",
  },
  {
    question: "Does this program guarantee migration to Australia?",
    answer:
      "No. This is an intake and preparation program only. It does not guarantee migration to Australia. Any later-stage progression remains subject to employer decisions, documentation requirements, legal and administrative processes, and any applicable destination-country rules.",
  },
  {
    question: "Why do you mention ethical recruitment?",
    answer:
      "Because international recruitment should be transparent, fair, and protective of workers. We want candidates to understand the process clearly, receive honest communication, and avoid misleading claims or unrealistic expectations about jobs, sponsorship, or migration outcomes.",
  },
];

const sectionFade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.55 },
};

const openHref = (href) => {
  if (!href) {
    return;
  }

  if (/^https?:\/\//.test(href)) {
    window.open(href, "_blank", "noopener,noreferrer");
    return;
  }

  window.location.href = href;
};

const SectionHeading = ({
  eyebrow,
  title,
  description,
  align = "left",
  theme = "light",
}) => (
  <div
    className={
      align === "center"
        ? "text-center max-w-3xl mx-auto mb-12"
        : "max-w-3xl mb-10"
    }
  >
    {eyebrow ? (
      <span
        className={`inline-flex items-center rounded-full text-xs font-medium px-4 py-2 mb-4 ${
          theme === "dark"
            ? "bg-white/10 text-white border border-white/15"
            : "bg-[#EAF0FF] text-[#2542D4]"
        }`}
      >
        {eyebrow}
      </span>
    ) : null}
    <h2
      className={`text-3xl md:text-4xl font-semibold mb-4 ${
        theme === "dark" ? "text-white" : "text-[#202356]"
      }`}
    >
      {title}
    </h2>
    {description ? (
      <p
        className={`text-base md:text-lg font-light leading-relaxed ${
          theme === "dark" ? "text-white/85" : "text-gray-600"
        }`}
      >
        {description}
      </p>
    ) : null}
  </div>
);

const Checklist = ({ items, columns = 1 }) => (
  <div className={`grid gap-4 ${columns === 2 ? "md:grid-cols-2" : ""}`}>
    {items.map((item) => (
      <div
        key={item}
        className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
      >
        <CheckCircle2
          size={20}
          className="text-[#2542D4] mt-0.5 flex-shrink-0"
        />
        <p className="text-sm md:text-base text-gray-700 font-light leading-relaxed">
          {item}
        </p>
      </div>
    ))}
  </div>
);

const AustraliaCareWorkforcePage = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const applicationsOpen = AUSTRALIA_CARE_WORKFORCE_APPLICATIONS_OPEN;

  const handleIntakeClick = () => {
    if (!applicationsOpen) {
      return;
    }

    openHref(AUSTRALIA_CARE_WORKFORCE_INTAKE_FORM);
  };

  const handleLearnHowItWorks = () => {
    document
      .getElementById("how-it-works")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-white">
      <section className="relative overflow-hidden bg-gradient-to-br from-[#F5F8FA] via-white to-[#EAF0FF]">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 h-64 w-64 rounded-full bg-[#2542D4]/10 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-[#202356]/10 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-[#C9D6FF] bg-white text-[#2542D4] text-xs font-medium px-4 py-2 mb-6">
                <Stethoscope size={14} />
                Australia Care Workforce Candidate Program
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#202356] leading-tight mb-6">
                Australia Health Care Assistant Candidate Program
              </h1>
              <p className="text-gray-600 text-base md:text-lg font-light leading-relaxed max-w-3xl mb-8">
                A structured pathway for candidate screening, preparation,
                assessment, and possible connection to suitable care workforce
                opportunities in Australia for qualified candidates.
              </p>

              {applicationsOpen ? null : (
                <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 mb-8">
                  <CalendarClock
                    size={20}
                    className="text-amber-600 mt-0.5 flex-shrink-0"
                  />
                  <div>
                    <p className="text-sm font-semibold text-[#202356]">
                      Applications are currently closed
                    </p>
                    <p className="text-sm text-gray-600 font-light leading-relaxed">
                      The current cohort has reached capacity. Intake will
                      reopen when the next cohort opens — please check back for
                      the next intake announcement.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4">
                {applicationsOpen ? (
                  <button
                    onClick={handleIntakeClick}
                    className="bg-[#202356] hover:bg-[#2d3270] text-white px-8 py-4 rounded-xl text-sm md:text-base transition-colors inline-flex items-center justify-center gap-2"
                  >
                    Complete Candidate Intake
                    <ArrowRight size={18} />
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="bg-gray-200 text-gray-500 px-8 py-4 rounded-xl text-sm md:text-base inline-flex items-center justify-center gap-2 cursor-not-allowed"
                  >
                    Intake Closed — Cohort at Capacity
                  </button>
                )}
                <button
                  onClick={handleLearnHowItWorks}
                  className="border border-[#202356] text-[#202356] hover:bg-[#202356] hover:text-white px-8 py-4 rounded-xl text-sm md:text-base transition-colors inline-flex items-center justify-center gap-2"
                >
                  Understand the Process
                </button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="bg-white border border-gray-200 rounded-[28px] p-6 md:p-8 shadow-xl shadow-[#202356]/5"
            >
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                {[
                  "Structured intake and review",
                  "Training and readiness preparation",
                  "Assessment and progression checks",
                  "Possible employer matching",
                ].map((item) => (
                  <div
                    key={item}
                    className="rounded-2xl bg-[#F5F8FA] p-4 border border-gray-100"
                  >
                    <CheckCircle2 size={18} className="text-[#2542D4] mb-3" />
                    <p className="text-sm text-gray-700 font-light leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl bg-[#202356] p-5 text-white">
                <p className="text-xs uppercase tracking-[0.18em] text-white/70 mb-2">
                  Important Notice
                </p>
                <p className="text-sm md:text-base font-light leading-relaxed">
                  Submitting an intake form does not constitute a job offer and
                  does not guarantee placement, sponsorship, migration, or
                  employment.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <motion.div {...sectionFade} className="max-w-4xl mx-auto px-4 md:px-8">
          <SectionHeading
            eyebrow="Intro"
            title="A structured intake and preparation pathway"
            description="This program is for individuals who want to be considered for an Australia-focused care workforce pathway. The program is designed to identify suitable candidates, prepare them, and connect qualified candidates to relevant opportunities where appropriate."
            align="center"
          />
        </motion.div>
      </section>

      <section className="py-16 bg-[#F9FBFF]">
        <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHeading
            eyebrow="What This Program Is"
            title="Early-stage identification and preparation for care-support pathways"
            description="This program focuses on identifying and preparing candidates for Health Care Assistant and related care-support opportunities in Australia."
          />
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 items-start">
            <div className="rounded-3xl bg-white border border-gray-200 p-6 md:p-8">
              <p className="text-gray-700 text-base font-light leading-relaxed mb-6">
                Depending on the pathway and employer needs, the process may
                include:
              </p>
              <Checklist items={programIncludes.slice(0, 7)} />
            </div>
            <div className="rounded-3xl bg-[#202356] text-white p-6 md:p-8">
              <h3 className="text-2xl font-semibold mb-4">
                What this page is not
              </h3>
              <p className="text-white/90 font-light leading-relaxed">
                This is not a general overseas jobs page. It is a structured
                intake and preparation page for candidates who want to be
                considered through a formal process.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      <section id="how-it-works" className="py-16 bg-white">
        <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHeading
            eyebrow="How The Program Works"
            title="Clear stages from intake to possible progression"
            description="A simple step-by-step view of how the program may work."
          />
          <div className="grid lg:grid-cols-2 gap-6">
            {programSteps.map((step, index) => (
              <div
                key={step.title}
                className="rounded-3xl border border-gray-200 bg-[#F5F8FA] p-6 md:p-7"
              >
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#202356] to-[#2542D4] text-white flex items-center justify-center text-lg font-semibold mb-5">
                  {index + 1}
                </div>
                <h3 className="text-xl font-semibold text-[#202356] mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600 font-light leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      <section className="py-16 bg-[#F9FBFF]">
        <motion.div
          {...sectionFade}
          className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-10"
        >
          <div>
            <SectionHeading
              eyebrow="Who This Program May Be Suitable For"
              title="Candidates open to formal screening, training, and relocation"
              description="This pathway may be suitable for individuals who:"
            />
            <Checklist items={suitabilityPoints} />
            <p className="text-gray-600 text-sm md:text-base font-light leading-relaxed mt-6">
              Relevant experience may be beneficial, but progression depends on
              suitability, screening outcomes, assessment results, and employer
              requirements.
            </p>
          </div>

          <div>
            <SectionHeading
              eyebrow="What The Program May Include"
              title="Support components that help measure and build readiness"
              description="The program may include the following components:"
            />
            <Checklist items={programIncludes} />
            <div className="mt-6 rounded-3xl border border-blue-200 bg-blue-50 p-6">
              <p className="text-gray-700 font-light leading-relaxed">
                Progression is not automatic. Advancement depends on
                performance, available opportunities, employer requirements,
                documentation, and other applicable conditions.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* <section className="py-16 bg-white">
       <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8">
         <SectionHeading
           eyebrow="Sponsored Participation"
           title="Current intake participation is intended to be fully sponsored for selected candidates"
           description="Where sponsorship applies, selected candidates will not be charged for participation at the current intake stage."
         />
         <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-8">
           <div className="rounded-3xl border border-gray-200 bg-[#F5F8FA] p-6 md:p-8">
             <p className="text-gray-700 font-light leading-relaxed mb-4">
               Any later stage involving third-party administrative, regulatory, travel, medical,
               visa, or destination-country requirements should be communicated clearly before a
               candidate decides to proceed.
             </p>
             <p className="text-gray-700 font-light leading-relaxed">
               Candidates should understand what is covered now and what may arise later.
             </p>
           </div>
           <div className="rounded-3xl bg-[#202356] text-white p-6 md:p-8">
             <h3 className="text-xl font-semibold mb-3">Transparency first</h3>
             <p className="text-white/85 font-light leading-relaxed">
               Clear communication about costs, stages, and expectations is part of the program
               approach.
             </p>
           </div>
         </div>
       </motion.div>
     </section> */}

      <section className="py-16 bg-[#F9FBFF]">
        <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHeading
            eyebrow="Ethical Recruitment"
            title="Ethical recruitment and worker protection are core to the program"
            description="We are committed to running this program in a way that is transparent, fair, and respectful of candidates."
          />
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 mb-10">
            {ethicalPrinciples.map((principle) => {
              const Icon = principle.icon;

              return (
                <div
                  key={principle.title}
                  className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#202356] to-[#2542D4] flex items-center justify-center mb-4">
                    <Icon size={22} className="text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#202356] mb-3">
                    {principle.title}
                  </h3>
                  <p className="text-gray-600 font-light leading-relaxed text-sm">
                    {principle.description}
                  </p>
                </div>
              );
            })}
          </div>
          {/* <div className="rounded-3xl border border-blue-200 bg-blue-50 p-6 md:p-8">
           <p className="text-gray-700 font-light leading-relaxed">
             Our approach is informed by recognized ethical recruitment and worker-protection
             standards.
           </p>
         </div> */}
        </motion.div>
      </section>

      <section className="py-16 bg-white">
        <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHeading
            eyebrow="Our Ethical Commitments"
            title="How we aim to protect candidate dignity and expectations"
            description="These commitments help keep the process clear, fair, and professional."
          />
          <div className="grid md:grid-cols-2 gap-6">
            {commitments.map((commitment, index) => (
              <div
                key={commitment.title}
                className="rounded-3xl bg-[#F5F8FA] border border-gray-200 p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-2xl bg-white border border-gray-200 flex items-center justify-center text-[#2542D4] font-semibold">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-semibold text-[#202356]">
                    {commitment.title}
                  </h3>
                </div>
                <p className="text-gray-600 font-light leading-relaxed">
                  {commitment.description}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      <section className="py-16 bg-[#F9FBFF]">
        <motion.div
          {...sectionFade}
          className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-10"
        >
          <div>
            <SectionHeading
              eyebrow="Candidate Safeguards"
              title="Candidates should expect clear information and professional treatment"
              description="Candidates should expect the following safeguards during the process:"
            />
            <Checklist items={safeguards} />
          </div>

          <div>
            <SectionHeading
              eyebrow="Important Notice"
              title="Intake does not equal employment"
              description="Submitting an intake form does not constitute a job offer and does not guarantee placement, sponsorship, migration, or employment."
            />
            <div className="rounded-3xl bg-[#202356] text-white p-6 md:p-8">
              <div className="flex items-start gap-3 mb-5">
                <CircleAlert size={22} className="mt-0.5 flex-shrink-0" />
                <p className="font-light leading-relaxed">
                  Progression depends on screening outcomes, assessment results,
                  candidate suitability, documentation, employer requirements,
                  legal processes, and available opportunities.
                </p>
              </div>
              <p className="text-white/80 text-sm font-light leading-relaxed">
                Candidates should only rely on formal written communication
                provided through the official process.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      <section className="py-16 bg-white">
        <motion.div
          {...sectionFade}
          className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-10 items-start"
        >
          <div>
            <SectionHeading
              eyebrow="Eligibility Snapshot"
              title="General readiness indicators for early review"
              description="Candidates may be expected to demonstrate:"
            />
            <Checklist items={eligibilitySnapshot} />
            <p className="text-gray-600 text-sm md:text-base font-light leading-relaxed mt-6">
              Meeting these points does not guarantee progression. They are only
              an initial snapshot.
            </p>
          </div>

          <div className="rounded-3xl border border-gray-200 bg-[#F5F8FA] p-6 md:p-8">
            <span className="inline-flex items-center rounded-full bg-white text-[#2542D4] text-xs font-medium px-4 py-2 mb-4">
              Why Ethics Matters
            </span>
            <h3 className="text-2xl font-semibold text-[#202356] mb-4">
              Cross-border recruitment should be rights-respecting from the
              start
            </h3>
            <p className="text-gray-700 font-light leading-relaxed mb-4">
              Because this pathway relates to international workforce
              opportunities, ethics should be built in from the start.
            </p>
            <p className="text-gray-700 font-light leading-relaxed">
              That is why this page does not promise employment and instead
              focuses on a clear, structured process.
            </p>
          </div>
        </motion.div>
      </section>

      <section className="py-16 bg-[#F9FBFF]">
        <motion.div {...sectionFade} className="max-w-4xl mx-auto px-4 md:px-8">
          <SectionHeading
            eyebrow="Frequently Asked Questions"
            title="Common questions about the candidate program"
            description="Short answers to the main questions candidates may have."
            align="center"
          />
          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = openFaqIndex === index;

              return (
                <div
                  key={faq.question}
                  className="rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between gap-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-base md:text-lg font-semibold text-[#202356]">
                      {faq.question}
                    </span>
                    <ChevronDown
                      size={20}
                      className={`text-[#2542D4] transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen ? (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 border-t border-gray-100 pt-4 text-gray-600 font-light leading-relaxed">
                          {faq.answer}
                        </div>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </motion.div>
      </section>

      <section className="py-16 bg-gradient-to-br from-[#202356] to-[#2542D4]">
        <motion.div
          {...sectionFade}
          className="max-w-5xl mx-auto px-4 md:px-8 text-center"
        >
          <SectionHeading
            eyebrow={applicationsOpen ? "Final CTA" : "Next Cohort"}
            title={
              applicationsOpen
                ? "Ready to Be Considered?"
                : "This Cohort Is at Capacity"
            }
            description={
              applicationsOpen
                ? "If you would like to be considered for this Australia-focused care workforce pathway, complete the official intake form."
                : "Applications for the current cohort are closed. Intake will reopen when the next cohort opens — please check back for the next intake announcement."
            }
            align="center"
            theme="dark"
          />
          <div className="flex justify-center">
            {applicationsOpen ? (
              <button
                onClick={handleIntakeClick}
                className="bg-white hover:bg-gray-100 text-[#202356] px-8 py-4 rounded-xl text-sm md:text-base transition-colors inline-flex items-center gap-2"
              >
                Complete Intake Form
                <ArrowRight size={18} />
              </button>
            ) : (
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 text-white px-8 py-4 text-sm md:text-base">
                <CalendarClock size={18} />
                Applications Closed
              </span>
            )}
          </div>
        </motion.div>
      </section>

      <section id="intake-form" className="py-16 bg-white">
        <motion.div {...sectionFade} className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="rounded-[32px] border border-dashed border-[#C9D6FF] bg-[#F5F8FA] p-8 md:p-10 text-center">
            {applicationsOpen ? (
              <>
                <ClipboardCheck
                  size={28}
                  className="mx-auto text-[#2542D4] mb-4"
                />
                <h2 className="text-3xl font-semibold text-[#202356] mb-4">
                  Start your screening application
                </h2>
                <p className="text-gray-600 font-light leading-relaxed max-w-2xl mx-auto mb-8">
                  Use the official intake route to share your background and
                  interest in the program.
                </p>
                <button
                  onClick={handleIntakeClick}
                  className="bg-[#202356] hover:bg-[#2d3270] text-white px-8 py-4 rounded-xl text-sm md:text-base transition-colors inline-flex items-center gap-2"
                >
                  Submit Intake Form
                  <ArrowRight size={18} />
                </button>
              </>
            ) : (
              <>
                <CalendarClock
                  size={28}
                  className="mx-auto text-[#2542D4] mb-4"
                />
                <h2 className="text-3xl font-semibold text-[#202356] mb-4">
                  Applications are currently closed
                </h2>
                <p className="text-gray-600 font-light leading-relaxed max-w-2xl mx-auto">
                  The current cohort has reached capacity, so new intake
                  submissions are paused. Applications will reopen when the next
                  cohort opens — please check back for the next intake
                  announcement.
                </p>
              </>
            )}
            <p className="text-xs text-gray-500 font-light leading-relaxed mt-6 max-w-3xl mx-auto">
              This page is for candidate intake and program information only. It
              is not a guarantee of employment, sponsorship, migration outcome,
              or placement.
            </p>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default AustraliaCareWorkforcePage;

// import React, { useState } from 'react';
// import { motion, AnimatePresence } from 'framer-motion';
// import {
//   ArrowRight,
//   BadgeCheck,
//   CheckCircle2,
//   ChevronDown,
//   CircleAlert,
//   ClipboardCheck,
//   HandHelping,
//   ShieldCheck,
//   Stethoscope,
//   Users
// } from 'lucide-react';
// import { AUSTRALIA_CARE_WORKFORCE_INTAKE_FORM } from '../constants';

// const programSteps = [
//   {
//     title: 'Submit Your Intake Form',
//     description:
//       'Complete the intake form with accurate details about your background, experience, communication skills, and readiness.'
//   },
//   {
//     title: 'Initial Screening',
//     description:
//       'Applications are reviewed for initial suitability. Shortlisted candidates may be contacted for communication checks, document review, and further screening.'
//   },
//   {
//     title: 'Training and Preparation',
//     description:
//       'Candidates who continue may go through structured preparation covering care-support roles, workplace expectations, communication, and readiness.'
//   },
//   {
//     title: 'Assessment',
//     description:
//       'Candidates may be assessed through practical, communication, behavioral, or role-readiness evaluations.'
//   },
//   {
//     title: 'Employer Review or Matching',
//     description:
//       'Candidates who meet the required standard may be profiled for suitable employer opportunities in Australia, where available.'
//   },
//   {
//     title: 'Next-Stage Guidance',
//     description:
//       'Candidates who progress may receive guidance on interviews, documentation, compliance, and later-stage requirements.'
//   }
// ];

// const programIncludes = [
//   'structured candidate intake',
//   'screening and suitability review',
//   'communication and readiness checks',
//   'preparation and training support',
//   'role orientation',
//   'assessment and evaluation',
//   'interview preparation where relevant',
//   'candidate presentation for employer consideration',
//   'guidance on next steps for candidates who progress'
// ];

// const suitabilityPoints = [
//   'have an interest in care-related work',
//   'have experience in elderly care, disability support, home care, hospital support, community care, or related service roles',
//   'are comfortable with personal care responsibilities where required',
//   'can communicate in English at a functional professional level',
//   'are open to structured training and assessment',
//   'are willing to follow a formal process',
//   'are open to relocation if selected and if all requirements are met'
// ];

// const ethicalPrinciples = [
//   {
//     icon: ShieldCheck,
//     title: 'Transparency',
//     description:
//       'We explain the program clearly, including what is being offered and what remains subject to review.'
//   },
//   {
//     icon: HandHelping,
//     title: 'Fair Treatment',
//     description:
//       'Candidates should experience respectful communication, professional screening, and clear expectations.'
//   },
//   {
//     icon: Users,
//     title: 'Worker Protection',
//     description:
//       'The process is designed to reduce misleading expectations and support informed participation.'
//   },
//   {
//     icon: BadgeCheck,
//     title: 'Responsible Process Design',
//     description:
//       'Progression is not automatic and remains subject to employer, legal, and documentation requirements.'
//   }
// ];

// const commitments = [
//   {
//     title: 'Clear and Honest Communication',
//     description:
//       'We will communicate the program clearly and will not present intake as a guaranteed job or migration outcome.'
//   },
//   {
//     title: 'No Misleading Claims',
//     description:
//       'We will avoid exaggerated or deceptive claims, including promises of guaranteed employment, visas, or relocation.'
//   },
//   {
//     title: 'Transparency on Process Stages',
//     description:
//       'We will explain the major stages clearly so candidates understand what is being offered and what is still under review.'
//   },
//   {
//     title: 'Respect for Candidate Dignity',
//     description:
//       'Candidates should be treated with respect, professionalism, and fairness throughout the process.'
//   },
//   {
//     title: 'Informed Decision-Making',
//     description:
//       'Candidates should have enough information to make informed decisions before moving forward.'
//   },
//   {
//     title: 'Responsible Handling of Expectations',
//     description:
//       'We distinguish clearly between intake, preparation, assessment, employer consideration, and any actual job offer.'
//   },
//   {
//     title: 'Protection Against Exploitative Practices',
//     description:
//       'We aim to reduce the risk of misinformation, coercion, unfair treatment, or exploitative recruitment behavior.'
//   },
//   {
//     title: 'Respect for Applicable Legal and Employer Requirements',
//     description:
//       'Any move into employment-related stages remains subject to employer, regulatory, and destination-country requirements.'
//   }
// ];

// const safeguards = [
//   'clear explanation of the program purpose',
//   'clear explanation that intake does not equal employment',
//   'clear explanation of the stages before any progression',
//   'communication of key conditions before major decisions are made',
//   'professional treatment during screening and assessment',
//   'opportunity to understand what is being evaluated',
//   'clear distinction between preparation stages and any later employer-led stage'
// ];

// const eligibilitySnapshot = [
//   'valid identification and supporting documentation',
//   'ability to complete the intake process truthfully and accurately',
//   'willingness to participate in screening and assessment',
//   'ability to communicate in English',
//   'readiness for structured preparation or training',
//   'professionalism and willingness to follow program instructions',
//   'openness to relocation if selected',
//   'ability to satisfy later employer and administrative requirements if progressed'
// ];

// const faqs = [
//   {
//     question: 'Is this a job offer?',
//     answer:
//       'No. This program is for candidate intake, screening, and possible progression through a structured program. It is not a job offer, and it should not be understood as a promise of employment, sponsorship, or relocation.'
//   },
//   {
//     question: 'Does submitting the form guarantee placement?',
//     answer:
//       'No. Submitting the intake form only means your details will be reviewed within the program process. It does not guarantee progression, placement, sponsorship, relocation, or employment. Advancement depends on screening outcomes, assessment performance, suitability, available opportunities, and employer requirements.'
//   },
//   {
//     question: 'Is participation free?',
//     answer:
//       'At the current intake stage, participation is intended to be fully sponsored for selected candidates. If any later stage involves third-party costs, such as documentation, medical, travel, regulatory, or destination-country requirements, those should be communicated clearly before a candidate decides whether to proceed.'
//   },
//   {
//     question: 'What roles is this for?',
//     answer:
//       'The present focus is on Health Care Assistant and related care-support pathways connected to Australia. Depending on employer needs, this may include care environments such as aged care, disability support, home care, hospital support, or similar service settings.'
//   },
//   {
//     question: 'What happens after I submit the form?',
//     answer:
//       'After submission, your intake will be reviewed to determine initial suitability. Candidates who are shortlisted may be contacted for further screening, communication checks, document review, or the next stage of the process. Not every applicant will progress beyond the initial review stage.'
//   },
//   {
//     question: 'Do I need prior experience?',
//     answer:
//       'Relevant experience can be helpful, especially in elderly care, disability support, home care, hospital support, community care, or related service roles. However, progression is based on overall suitability, communication ability, readiness, assessment outcomes, and the needs of the pathway or participating employers.'
//   },
//   {
//     question: 'Does this program guarantee migration to Australia?',
//     answer:
//       'No. This is an intake and preparation program only. It does not guarantee migration to Australia. Any later-stage progression remains subject to employer decisions, documentation requirements, legal and administrative processes, and any applicable destination-country rules.'
//   },
//   {
//     question: 'Why do you mention ethical recruitment?',
//     answer:
//       'Because international recruitment should be transparent, fair, and protective of workers. We want candidates to understand the process clearly, receive honest communication, and avoid misleading claims or unrealistic expectations about jobs, sponsorship, or migration outcomes.'
//   }
// ];

// const sectionFade = {
//   initial: { opacity: 0, y: 24 },
//   whileInView: { opacity: 1, y: 0 },
//   viewport: { once: true, amount: 0.2 },
//   transition: { duration: 0.55 }
// };

// const openHref = (href) => {
//   if (!href) {
//     return;
//   }

//   if (/^https?:\/\//.test(href)) {
//     window.open(href, '_blank', 'noopener,noreferrer');
//     return;
//   }

//   window.location.href = href;
// };

// const SectionHeading = ({ eyebrow, title, description, align = 'left', theme = 'light' }) => (
//   <div className={align === 'center' ? 'text-center max-w-3xl mx-auto mb-12' : 'max-w-3xl mb-10'}>
//     {eyebrow ? (
//       <span
//         className={`inline-flex items-center rounded-full text-xs font-medium px-4 py-2 mb-4 ${
//           theme === 'dark'
//             ? 'bg-white/10 text-white border border-white/15'
//             : 'bg-[#EAF0FF] text-[#2542D4]'
//         }`}
//       >
//         {eyebrow}
//       </span>
//     ) : null}
//     <h2
//       className={`text-3xl md:text-4xl font-semibold mb-4 ${
//         theme === 'dark' ? 'text-white' : 'text-[#202356]'
//       }`}
//     >
//       {title}
//     </h2>
//     {description ? (
//       <p
//         className={`text-base md:text-lg font-light leading-relaxed ${
//           theme === 'dark' ? 'text-white/85' : 'text-gray-600'
//         }`}
//       >
//         {description}
//       </p>
//     ) : null}
//   </div>
// );

// const Checklist = ({ items, columns = 1 }) => (
//   <div className={`grid gap-4 ${columns === 2 ? 'md:grid-cols-2' : ''}`}>
//     {items.map((item) => (
//       <div
//         key={item}
//         className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
//       >
//         <CheckCircle2 size={20} className="text-[#2542D4] mt-0.5 flex-shrink-0" />
//         <p className="text-sm md:text-base text-gray-700 font-light leading-relaxed">{item}</p>
//       </div>
//     ))}
//   </div>
// );

// const AustraliaCareWorkforcePage = () => {
//   const [openFaqIndex, setOpenFaqIndex] = useState(0);

//   const handleIntakeClick = () => {
//     openHref(AUSTRALIA_CARE_WORKFORCE_INTAKE_FORM);
//   };

//   const handleLearnHowItWorks = () => {
//     document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
//   };

//   return (
//     <div className="bg-white">
//       <section className="relative overflow-hidden bg-gradient-to-br from-[#F5F8FA] via-white to-[#EAF0FF]">
//         <div className="absolute inset-0 pointer-events-none">
//           <div className="absolute top-0 right-0 h-64 w-64 rounded-full bg-[#2542D4]/10 blur-3xl" />
//           <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-[#202356]/10 blur-3xl" />
//         </div>

//         <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
//           <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
//             <motion.div
//               initial={{ opacity: 0, y: 24 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.6 }}
//             >
//               <span className="inline-flex items-center gap-2 rounded-full border border-[#C9D6FF] bg-white text-[#2542D4] text-xs font-medium px-4 py-2 mb-6">
//                 <Stethoscope size={14} />
//                 Australia Care Workforce Candidate Program
//               </span>
//               <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#202356] leading-tight mb-6">
//                 Australia Health Care Assistant Candidate Program
//               </h1>
//               <p className="text-gray-600 text-base md:text-lg font-light leading-relaxed max-w-3xl mb-8">
//                 A structured pathway for candidate screening, preparation, assessment, and possible
//                 connection to suitable care workforce opportunities in Australia for qualified
//                 candidates.
//               </p>

//               <div className="flex flex-col sm:flex-row gap-4">
//                 <button
//                   onClick={handleIntakeClick}
//                   className="bg-[#202356] hover:bg-[#2d3270] text-white px-8 py-4 rounded-xl text-sm md:text-base transition-colors inline-flex items-center justify-center gap-2"
//                 >
//                   Complete Candidate Intake
//                   <ArrowRight size={18} />
//                 </button>
//                 <button
//                   onClick={handleLearnHowItWorks}
//                   className="border border-[#202356] text-[#202356] hover:bg-[#202356] hover:text-white px-8 py-4 rounded-xl text-sm md:text-base transition-colors inline-flex items-center justify-center gap-2"
//                 >
//                   Understand the Process
//                 </button>
//               </div>
//             </motion.div>

//             <motion.div
//               initial={{ opacity: 0, y: 24 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.6, delay: 0.15 }}
//               className="bg-white border border-gray-200 rounded-[28px] p-6 md:p-8 shadow-xl shadow-[#202356]/5"
//             >
//               <div className="grid sm:grid-cols-2 gap-4 mb-6">
//                 {[
//                   'Structured intake and review',
//                   'Training and readiness preparation',
//                   'Assessment and progression checks',
//                   'Possible employer matching'
//                 ].map((item) => (
//                   <div key={item} className="rounded-2xl bg-[#F5F8FA] p-4 border border-gray-100">
//                     <CheckCircle2 size={18} className="text-[#2542D4] mb-3" />
//                     <p className="text-sm text-gray-700 font-light leading-relaxed">{item}</p>
//                   </div>
//                 ))}
//               </div>

//               <div className="rounded-2xl bg-[#202356] p-5 text-white">
//                 <p className="text-xs uppercase tracking-[0.18em] text-white/70 mb-2">
//                   Important Notice
//                 </p>
//                 <p className="text-sm md:text-base font-light leading-relaxed">
//                   Submitting an intake form does not constitute a job offer and does not guarantee
//                   placement, sponsorship, migration, or employment.
//                 </p>
//               </div>
//             </motion.div>
//           </div>
//         </div>
//       </section>

//       <section className="py-16 bg-white">
//         <motion.div {...sectionFade} className="max-w-4xl mx-auto px-4 md:px-8">
//           <SectionHeading
//             eyebrow="Intro"
//             title="A structured intake and preparation pathway"
//             description="This program is for individuals who want to be considered for an Australia-focused care workforce pathway. The program is designed to identify suitable candidates, prepare them, and connect qualified candidates to relevant opportunities where appropriate."
//             align="center"
//           />

//         </motion.div>
//       </section>

//       <section className="py-16 bg-[#F9FBFF]">
//         <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8">
//           <SectionHeading
//             eyebrow="What This Program Is"
//             title="Early-stage identification and preparation for care-support pathways"
//             description="This program focuses on identifying and preparing candidates for Health Care Assistant and related care-support opportunities in Australia."
//           />
//           <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 items-start">
//             <div className="rounded-3xl bg-white border border-gray-200 p-6 md:p-8">
//               <p className="text-gray-700 text-base font-light leading-relaxed mb-6">
//                 Depending on the pathway and employer needs, the process may include:
//               </p>
//               <Checklist items={programIncludes.slice(0, 7)} />
//             </div>
//             <div className="rounded-3xl bg-[#202356] text-white p-6 md:p-8">
//               <h3 className="text-2xl font-semibold mb-4">What this page is not</h3>
//               <p className="text-white/90 font-light leading-relaxed">
//                 This is not a general overseas jobs page. It is a structured intake and preparation
//                 page for candidates who want to be considered through a formal process.
//               </p>
//             </div>
//           </div>
//         </motion.div>
//       </section>

//       <section id="how-it-works" className="py-16 bg-white">
//         <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8">
//           <SectionHeading
//             eyebrow="How The Program Works"
//             title="Clear stages from intake to possible progression"
//             description="A simple step-by-step view of how the program may work."
//           />
//           <div className="grid lg:grid-cols-2 gap-6">
//             {programSteps.map((step, index) => (
//               <div
//                 key={step.title}
//                 className="rounded-3xl border border-gray-200 bg-[#F5F8FA] p-6 md:p-7"
//               >
//                 <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#202356] to-[#2542D4] text-white flex items-center justify-center text-lg font-semibold mb-5">
//                   {index + 1}
//                 </div>
//                 <h3 className="text-xl font-semibold text-[#202356] mb-3">{step.title}</h3>
//                 <p className="text-gray-600 font-light leading-relaxed">{step.description}</p>
//               </div>
//             ))}
//           </div>
//         </motion.div>
//       </section>

//       <section className="py-16 bg-[#F9FBFF]">
//         <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-10">
//           <div>
//             <SectionHeading
//               eyebrow="Who This Program May Be Suitable For"
//               title="Candidates open to formal screening, training, and relocation"
//               description="This pathway may be suitable for individuals who:"
//             />
//             <Checklist items={suitabilityPoints} />
//             <p className="text-gray-600 text-sm md:text-base font-light leading-relaxed mt-6">
//               Relevant experience may be beneficial, but progression depends on suitability,
//               screening outcomes, assessment results, and employer requirements.
//             </p>
//           </div>

//           <div>
//             <SectionHeading
//               eyebrow="What The Program May Include"
//               title="Support components that help measure and build readiness"
//               description="The program may include the following components:"
//             />
//             <Checklist items={programIncludes} />
//             <div className="mt-6 rounded-3xl border border-blue-200 bg-blue-50 p-6">
//               <p className="text-gray-700 font-light leading-relaxed">
//                 Progression is not automatic. Advancement depends on performance, available
//                 opportunities, employer requirements, documentation, and other applicable
//                 conditions.
//               </p>
//             </div>
//           </div>
//         </motion.div>
//       </section>

//       {/* <section className="py-16 bg-white">
//         <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8">
//           <SectionHeading
//             eyebrow="Sponsored Participation"
//             title="Current intake participation is intended to be fully sponsored for selected candidates"
//             description="Where sponsorship applies, selected candidates will not be charged for participation at the current intake stage."
//           />
//           <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-8">
//             <div className="rounded-3xl border border-gray-200 bg-[#F5F8FA] p-6 md:p-8">
//               <p className="text-gray-700 font-light leading-relaxed mb-4">
//                 Any later stage involving third-party administrative, regulatory, travel, medical,
//                 visa, or destination-country requirements should be communicated clearly before a
//                 candidate decides to proceed.
//               </p>
//               <p className="text-gray-700 font-light leading-relaxed">
//                 Candidates should understand what is covered now and what may arise later.
//               </p>
//             </div>
//             <div className="rounded-3xl bg-[#202356] text-white p-6 md:p-8">
//               <h3 className="text-xl font-semibold mb-3">Transparency first</h3>
//               <p className="text-white/85 font-light leading-relaxed">
//                 Clear communication about costs, stages, and expectations is part of the program
//                 approach.
//               </p>
//             </div>
//           </div>
//         </motion.div>
//       </section> */}

//       <section className="py-16 bg-[#F9FBFF]">
//         <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8">
//           <SectionHeading
//             eyebrow="Ethical Recruitment"
//             title="Ethical recruitment and worker protection are core to the program"
//             description="We are committed to running this program in a way that is transparent, fair, and respectful of candidates."
//           />
//           <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 mb-10">
//             {ethicalPrinciples.map((principle) => {
//               const Icon = principle.icon;

//               return (
//                 <div
//                   key={principle.title}
//                   className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm"
//                 >
//                   <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#202356] to-[#2542D4] flex items-center justify-center mb-4">
//                     <Icon size={22} className="text-white" />
//                   </div>
//                   <h3 className="text-lg font-semibold text-[#202356] mb-3">{principle.title}</h3>
//                   <p className="text-gray-600 font-light leading-relaxed text-sm">
//                     {principle.description}
//                   </p>
//                 </div>
//               );
//             })}
//           </div>
//           {/* <div className="rounded-3xl border border-blue-200 bg-blue-50 p-6 md:p-8">
//             <p className="text-gray-700 font-light leading-relaxed">
//               Our approach is informed by recognized ethical recruitment and worker-protection
//               standards.
//             </p>
//           </div> */}
//         </motion.div>
//       </section>

//       <section className="py-16 bg-white">
//         <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8">
//           <SectionHeading
//             eyebrow="Our Ethical Commitments"
//             title="How we aim to protect candidate dignity and expectations"
//             description="These commitments help keep the process clear, fair, and professional."
//           />
//           <div className="grid md:grid-cols-2 gap-6">
//             {commitments.map((commitment, index) => (
//               <div key={commitment.title} className="rounded-3xl bg-[#F5F8FA] border border-gray-200 p-6">
//                 <div className="flex items-center gap-3 mb-4">
//                   <div className="w-10 h-10 rounded-2xl bg-white border border-gray-200 flex items-center justify-center text-[#2542D4] font-semibold">
//                     {index + 1}
//                   </div>
//                   <h3 className="text-lg font-semibold text-[#202356]">{commitment.title}</h3>
//                 </div>
//                 <p className="text-gray-600 font-light leading-relaxed">{commitment.description}</p>
//               </div>
//             ))}
//           </div>
//         </motion.div>
//       </section>

//       <section className="py-16 bg-[#F9FBFF]">
//         <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-10">
//           <div>
//           <SectionHeading
//             eyebrow="Candidate Safeguards"
//             title="Candidates should expect clear information and professional treatment"
//             description="Candidates should expect the following safeguards during the process:"
//           />
//             <Checklist items={safeguards} />
//           </div>

//           <div>
//             <SectionHeading
//               eyebrow="Important Notice"
//               title="Intake does not equal employment"
//               description="Submitting an intake form does not constitute a job offer and does not guarantee placement, sponsorship, migration, or employment."
//             />
//             <div className="rounded-3xl bg-[#202356] text-white p-6 md:p-8">
//               <div className="flex items-start gap-3 mb-5">
//                 <CircleAlert size={22} className="mt-0.5 flex-shrink-0" />
//                 <p className="font-light leading-relaxed">
//                   Progression depends on screening outcomes, assessment results, candidate
//                   suitability, documentation, employer requirements, legal processes, and available
//                   opportunities.
//                 </p>
//               </div>
//               <p className="text-white/80 text-sm font-light leading-relaxed">
//                 Candidates should only rely on formal written communication provided through the
//                 official process.
//               </p>
//             </div>
//           </div>
//         </motion.div>
//       </section>

//       <section className="py-16 bg-white">
//         <motion.div {...sectionFade} className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-10 items-start">
//           <div>
//             <SectionHeading
//               eyebrow="Eligibility Snapshot"
//               title="General readiness indicators for early review"
//               description="Candidates may be expected to demonstrate:"
//             />
//             <Checklist items={eligibilitySnapshot} />
//             <p className="text-gray-600 text-sm md:text-base font-light leading-relaxed mt-6">
//               Meeting these points does not guarantee progression. They are only an initial
//               snapshot.
//             </p>
//           </div>

//           <div className="rounded-3xl border border-gray-200 bg-[#F5F8FA] p-6 md:p-8">
//             <span className="inline-flex items-center rounded-full bg-white text-[#2542D4] text-xs font-medium px-4 py-2 mb-4">
//               Why Ethics Matters
//             </span>
//             <h3 className="text-2xl font-semibold text-[#202356] mb-4">
//               Cross-border recruitment should be rights-respecting from the start
//             </h3>
//             <p className="text-gray-700 font-light leading-relaxed mb-4">
//               Because this pathway relates to international workforce opportunities, ethics should
//               be built in from the start.
//             </p>
//             <p className="text-gray-700 font-light leading-relaxed">
//               That is why this page does not promise employment and instead focuses on a clear,
//               structured process.
//             </p>
//           </div>
//         </motion.div>
//       </section>

//       <section className="py-16 bg-[#F9FBFF]">
//         <motion.div {...sectionFade} className="max-w-4xl mx-auto px-4 md:px-8">
//           <SectionHeading
//             eyebrow="Frequently Asked Questions"
//             title="Common questions about the candidate program"
//             description="Short answers to the main questions candidates may have."
//             align="center"
//           />
//           <div className="space-y-4">
//             {faqs.map((faq, index) => {
//               const isOpen = openFaqIndex === index;

//               return (
//                 <div
//                   key={faq.question}
//                   className="rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden"
//                 >
//                   <button
//                     onClick={() => setOpenFaqIndex(isOpen ? null : index)}
//                     className="w-full px-6 py-5 flex items-center justify-between gap-4 text-left hover:bg-gray-50 transition-colors"
//                   >
//                     <span className="text-base md:text-lg font-semibold text-[#202356]">
//                       {faq.question}
//                     </span>
//                     <ChevronDown
//                       size={20}
//                       className={`text-[#2542D4] transition-transform ${isOpen ? 'rotate-180' : ''}`}
//                     />
//                   </button>
//                   <AnimatePresence initial={false}>
//                     {isOpen ? (
//                       <motion.div
//                         initial={{ height: 0, opacity: 0 }}
//                         animate={{ height: 'auto', opacity: 1 }}
//                         exit={{ height: 0, opacity: 0 }}
//                         transition={{ duration: 0.25 }}
//                         className="overflow-hidden"
//                       >
//                         <div className="px-6 pb-6 border-t border-gray-100 pt-4 text-gray-600 font-light leading-relaxed">
//                           {faq.answer}
//                         </div>
//                       </motion.div>
//                     ) : null}
//                   </AnimatePresence>
//                 </div>
//               );
//             })}
//           </div>
//         </motion.div>
//       </section>

//       <section className="py-16 bg-gradient-to-br from-[#202356] to-[#2542D4]">
//         <motion.div {...sectionFade} className="max-w-5xl mx-auto px-4 md:px-8 text-center">
//           <SectionHeading
//             eyebrow="Final CTA"
//             title="Ready to Be Considered?"
//             description="If you would like to be considered for this Australia-focused care workforce pathway, complete the official intake form."
//             align="center"
//             theme="dark"
//           />
//           <div className="flex justify-center">
//             <button
//               onClick={handleIntakeClick}
//               className="bg-white hover:bg-gray-100 text-[#202356] px-8 py-4 rounded-xl text-sm md:text-base transition-colors inline-flex items-center gap-2"
//             >
//               Complete Intake Form
//               <ArrowRight size={18} />
//             </button>
//           </div>
//         </motion.div>
//       </section>

//       <section id="intake-form" className="py-16 bg-white">
//         <motion.div {...sectionFade} className="max-w-4xl mx-auto px-4 md:px-8">
//           <div className="rounded-[32px] border border-dashed border-[#C9D6FF] bg-[#F5F8FA] p-8 md:p-10 text-center">
//             <ClipboardCheck size={28} className="mx-auto text-[#2542D4] mb-4" />
//             <h2 className="text-3xl font-semibold text-[#202356] mb-4">Start your screening application</h2>
//             <p className="text-gray-600 font-light leading-relaxed max-w-2xl mx-auto mb-8">
//               Use the official intake route to share your background and interest in the program.
//             </p>
//             <button
//               onClick={handleIntakeClick}
//               className="bg-[#202356] hover:bg-[#2d3270] text-white px-8 py-4 rounded-xl text-sm md:text-base transition-colors inline-flex items-center gap-2"
//             >
//               Submit Intake Form
//               <ArrowRight size={18} />
//             </button>
//             <p className="text-xs text-gray-500 font-light leading-relaxed mt-6 max-w-3xl mx-auto">
//               This page is for candidate intake and program information only. It is not a
//               guarantee of employment, sponsorship, migration outcome, or placement.
//             </p>
//           </div>
//         </motion.div>
//       </section>
//     </div>
//   );
// };

// export default AustraliaCareWorkforcePage;
